import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { JupiterStateFeatureSupportModel, JupiterStateModel, LabelsSlot } from './types';
import { RelatedToType } from '@shared/enums';

export const isAddFormDefinitionPersisted = (jupiterState: JupiterStateModel): boolean => {
  return (
    jupiterState.isCustom ||
    [RelatedToType.PropertyTenant, RelatedToType.Company, RelatedToType.Contact, RelatedToType.Property].includes(
      jupiterState.relatedToObject.name as RelatedToType,
    )
  );
};

export const getLabelSlots = (tsObject: CustomObject): LabelsSlot => {
  return {
    ADD: tsObject.label ? `Add new ${tsObject.label}` : 'Add',
    EXPORT_CSV: 'Export as CSV',
    IMPORT_VCARD: 'Import VCard',
    BULK_ACTIONS: tsObject.name === RelatedToType.Property ? 'Create Deal or Portfolio' : 'Update Selected',
  };
};

export const getFeatureSupport = (obj: CustomObject): JupiterStateFeatureSupportModel => {
  if (obj.id) {
    return {
      groupedTable: false,
      bulkActions: {
        enabled: false,
        actions: {
          deleteRows: false,
          updateTags: false,
          createDealOrPortfolio: false,
        },
      },
      homeActions: {
        actions: {
          add: true,
          export: true,
          importVCard: false,
          createContactAssociation: false,
          manageView: true,
          viewList: true,
          linkDeal: false,
        },
      },
      gridActions: {
        enabled: true,
        actions: {
          logCall: false,
          logMeeting: false,
          logSocialMedia: false,
          edit: true,
          delete: true,
          openDetails: true,
        },
      },
      tags: {
        enabled: false,
      },
      detailsMainTabs: {
        backButton: true,
        avatar: false,
        details: true,
        files: true,
        notes: true,
        activities: true,
        engagements: false,
        tasks: true,
        linkedObjects: true,
        deals: false,
        leases: false,
        nearby: false,
        contacts: false,
        dispositions: false,
        loanQuotes: false,
        financials: false,
        contextMenu: {
          enabled: true,
          actions: {
            layoutManager: true,
            delete: true,
            duplicate: true,
            changeLayout: true,
            generateMergeDocument: true,
            merge: false,
            sendEmail: false,
            summary: false,
          },
        },
      },
      panels: {
        'crm-table-panel': false,
        'crm-action-panel': false,
        form: true,
        comments: true,
        table: true,
        images: true,
        matrix: true,
        activity: true,
        engagements: true,
        map: false,
        chart: false,
        financial_report: false,
        roles: false,
        contacts: false,
        unit_lease: false,
        unit_form: false,
        mailbox: false,
      },
    };
  }

  const isPropertyNearby = obj.uiConfig?.urlPrefix === 'property-nearby';
  const isPropertyDeals = obj.uiConfig?.urlPrefix === 'property-deals';
  const isCrm = [RelatedToType.Contact, RelatedToType.Company].includes(obj.name as RelatedToType);
  const isContact = RelatedToType.Contact === (obj.name as RelatedToType);
  const canShowLeases = ![RelatedToType.Contact, RelatedToType.Company, RelatedToType.PropertyTenant].includes(
    obj.name as RelatedToType,
  );
  const canShowGalleryPanel = canShowLeases;
  const canShowMatrixPanel = true;
  const canShowTablePanel = RelatedToType.PropertyTenant !== obj.name;
  const canShowAddButton = RelatedToType.PropertyTenant !== obj.name && !isPropertyNearby;
  const canShowAssetManagement = [RelatedToType.Property, RelatedToType.PropertyTenant].includes(
    obj.name as RelatedToType,
  );

  return {
    groupedTable: false,
    bulkActions: {
      enabled: RelatedToType.PropertyTenant !== obj.name,
      actions: {
        deleteRows: false,
        updateTags: isCrm,
        createDealOrPortfolio: RelatedToType.Property === obj.name,
      },
    },
    homeActions: {
      actions: {
        add: canShowAddButton,
        export: !isPropertyNearby && !isPropertyDeals,
        importVCard: isContact && !obj.uiConfig?.isForStandardRelation,
        createContactAssociation: RelatedToType.Contact === obj.name && obj.uiConfig?.isForStandardRelation,
        manageView: !isPropertyNearby && !isPropertyDeals,
        viewList: !isPropertyNearby && !isPropertyDeals,
        linkDeal: isPropertyDeals,
      },
    },
    gridActions: {
      enabled: !canShowAssetManagement,
      actions: {
        logCall: isCrm,
        logMeeting: isCrm,
        logSocialMedia: isCrm,
        edit: false,
        delete: false,
        openDetails: false,
      },
    },
    tags: {
      enabled: isCrm,
    },
    detailsMainTabs: {
      backButton: true,
      avatar: isCrm,
      details: true,
      files: true,
      notes: true,
      activities: true,
      engagements: isCrm,
      tasks: RelatedToType.PropertyTenant !== obj.name,
      linkedObjects: RelatedToType.PropertyTenant !== obj.name,
      leases: canShowLeases,
      deals: RelatedToType.Property === obj.name,
      nearby: RelatedToType.Property === obj.name,
      loanQuotes: RelatedToType.Project === obj.name,
      dispositions: RelatedToType.Project === obj.name,
      contacts: RelatedToType.Project === obj.name,
      financials: canShowAssetManagement,
      contextMenu: {
        enabled: true,
        actions: {
          layoutManager: true,
          delete: true,
          duplicate: false,
          changeLayout: true,
          generateMergeDocument: RelatedToType.Property === obj.name,
          merge: isCrm,
          sendEmail: RelatedToType.Contact === obj.name,
          summary: RelatedToType.Property === obj.name,
        },
      },
    },
    panels: {
      'crm-table-panel': isCrm || RelatedToType.Property === obj.name, // I think I will refactor this soon
      'crm-action-panel': isCrm,
      form: true,
      comments: true,
      table: canShowTablePanel,
      images: canShowGalleryPanel,
      matrix: canShowMatrixPanel,
      activity: true,
      engagements: isCrm,
      map: RelatedToType.Property === obj.name,
      chart: canShowAssetManagement,
      financial_report: canShowAssetManagement,
      roles: [RelatedToType.Property, RelatedToType.Project].includes(obj.name as RelatedToType),
      contacts: RelatedToType.Property === obj.name,
      unit_lease: RelatedToType.Property === obj.name,
      unit_form: RelatedToType.Property === obj.name,
      mailbox: isCrm,
    },
  };
};
